import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getLinkContext, verifyLink } from '@/services/api';
import { parseHostedError } from '@/services/errors';
import session from '@/session';

/**
 * Shared entry logic for both hosted flows: adopt a webview hand-off token,
 * resolve the email-link context (school branding + masked email), run the
 * last-name verification, and fall back to verify when the 60-min session
 * expires mid-visit.
 *
 * phase: 'loading' | 'verify' | 'ready' | 'dead' | 'request-link'
 */
const useHostedEntry = () => {
	const [searchParams] = useSearchParams();
	const t = searchParams.get('t');

	const [phase, setPhase] = useState('loading');
	const [context, setContext] = useState(null);
	const [deadVariant, setDeadVariant] = useState(null);
	const [verifyError, setVerifyError] = useState(null);
	const [verifyBusy, setVerifyBusy] = useState(false);
	const [notice, setNotice] = useState(null);

	useEffect(() => {
		let cancelled = false;

		const boot = async () => {
			session.adoptFromUrl();
			const active = session.restore();

			// With a live session the status endpoint supplies branding + the
			// student's name — skip the (10/min-throttled) pre-auth context
			// call entirely so refresh-heavy visits never burn the limiter.
			if (active) {
				setPhase('ready');
				return;
			}

			if (!t) {
				// Bare visit — never had a token or a session. Distinct from
				// 'no_session' (a webview session that expired mid-visit).
				setDeadVariant('link_required');
				setPhase('dead');
				return;
			}

			try {
				const ctx = await getLinkContext(t);
				if (cancelled) return;
				setContext(ctx);
				setPhase('verify');
			} catch (err) {
				if (cancelled) return;
				const parsed = parseHostedError(err);
				setDeadVariant(parsed.type === 'throttled' ? 'link_not_found' : (parsed.code ?? 'link_not_found'));
				setPhase('dead');
			}
		};

		boot();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [t]);

	// Global 401 → drop back to verify (or a dead end when there's no link
	// token to re-verify against, i.e. the webview path).
	useEffect(() => {
		const onExpired = () => {
			if (t) {
				setNotice('Your session expired — please verify again.');
				setPhase('verify');
				// Boot skipped the context call while the session was live —
				// the verify screen needs the branding + masked email now.
				getLinkContext(t)
					.then(setContext)
					.catch(() => {});
			} else {
				setDeadVariant('no_session');
				setPhase('dead');
			}
		};
		window.addEventListener('pay:session-expired', onExpired);
		return () => window.removeEventListener('pay:session-expired', onExpired);
	}, [t]);

	// Dead ends offer a self-serve "email me a secure link" path (expired
	// emailed links land on 'dead', tokenless visits on 'link_required').
	const openRequestLink = useCallback(() => setPhase('request-link'), []);
	const closeRequestLink = useCallback(() => setPhase('dead'), []);

	const verify = useCallback(
		async (lastName) => {
			setVerifyBusy(true);
			setVerifyError(null);
			try {
				const result = await verifyLink(t, lastName.trim());
				session.save({ ...result, webview: false });
				setNotice(null);
				setPhase('ready');
			} catch (err) {
				const parsed = parseHostedError(err);
				if (parsed.type === 'dead_link') {
					setDeadVariant(parsed.code);
					setPhase('dead');
				} else {
					setVerifyError(parsed.message);
				}
			} finally {
				setVerifyBusy(false);
			}
		},
		[t]
	);

	return {
		phase,
		context,
		deadVariant,
		verify,
		verifyError,
		verifyBusy,
		notice,
		openRequestLink,
		closeRequestLink,
	};
};

export default useHostedEntry;
